import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { formatCurrency } from '../../Succession/Financing/financingCalculations';

export function formatCompact(value) {
  if (!value && value !== 0) return '$0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-card border border-border rounded-[8px] p-3 shadow-md">
      <p className="font-mono text-[11px] text-muted-foreground mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-[13px] text-foreground">
          <span className="capitalize">{entry.dataKey}</span>: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

// NOTE: This component is currently 100% presentational — `readOnly` is
// accepted defensively so the deal team workspace's "Show owner's view" modal
// can pass it consistently with other Succeedo result components, and so that
// any future interactive affordances added here are gated by default.
// eslint-disable-next-line no-unused-vars
const FinancingResultsPanel = ({ calculations, loanAmount, interestRate, loanTerm, readOnly }) => {
  if (!calculations || loanAmount <= 0) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-[16px] p-6">
          <p className="text-muted-foreground text-[14px]">
            Enter a purchase price to see results.
          </p>
        </div>
      </div>
    );
  }

  const { monthlyPayment, totalInterest, totalCost, schedule } = calculations;
  const months = loanTerm * 12;
  const displayRate = (interestRate * 100).toFixed(1);

  const chartData = schedule.map((yearData) => ({
    year: `YR ${yearData.year}`,
    principal: Math.round(yearData.totalPrincipal),
    interest: Math.round(yearData.totalInterest),
  }));

  return (
    <div className="space-y-6">
      {/* Monthly Payment Card */}
      <div className="bg-card border border-border rounded-[16px] p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            MONTHLY PAYMENT
          </span>
        </div>
        <div className="font-serif text-[56px] font-light text-foreground mb-2">
          {formatCurrency(monthlyPayment)}
        </div>
        <p className="text-[14px] text-muted-foreground">
          over {months} months at {displayRate}%
        </p>
      </div>

      {/* Three Metric Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-card border border-border rounded-[12px] p-3 sm:p-4">
          <div className="font-mono text-[10px] sm:text-[11px] uppercase text-muted-foreground mb-2">
            TOTAL INTEREST
          </div>
          <div className="font-serif text-[20px] sm:text-[28px] font-light text-foreground">
            <span className="sm:hidden">{formatCompact(totalInterest)}</span>
            <span className="hidden sm:inline">{formatCurrency(totalInterest)}</span>
          </div>
        </div>
        <div className="bg-card border border-border rounded-[12px] p-3 sm:p-4">
          <div className="font-mono text-[10px] sm:text-[11px] uppercase text-muted-foreground mb-2">
            LOAN AMOUNT
          </div>
          <div className="font-serif text-[20px] sm:text-[28px] font-light text-foreground">
            <span className="sm:hidden">{formatCompact(loanAmount)}</span>
            <span className="hidden sm:inline">{formatCurrency(loanAmount)}</span>
          </div>
        </div>
        <div className="bg-card border border-border rounded-[12px] p-3 sm:p-4">
          <div className="font-mono text-[10px] sm:text-[11px] uppercase text-muted-foreground mb-2">
            TOTAL COST
          </div>
          <div className="font-serif text-[20px] sm:text-[28px] font-light text-foreground mb-1">
            <span className="sm:hidden">{formatCompact(totalCost)}</span>
            <span className="hidden sm:inline">{formatCurrency(totalCost)}</span>
          </div>
          <div className="font-mono text-[10px] sm:text-[11px] text-muted-foreground hidden sm:block">
            Purchase + interest
          </div>
        </div>
      </div>

      {/* Amortization Chart */}
      <div className="bg-card border border-border rounded-[16px] p-6">
        <div className="flex items-center gap-2 mb-6">
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            AMORTIZATION SCHEDULE
          </span>
        </div>

        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData}>
            <XAxis
              dataKey="year"
              tick={{
                fontSize: 10,
                fontFamily: 'var(--font-mono, monospace)',
                fill: 'hsl(var(--muted-foreground) / 0.4)',
              }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="principal" stackId="a" radius={[0, 0, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell key={`principal-${index}`} fill="hsl(var(--succeedo-orange))" />
              ))}
            </Bar>
            <Bar dataKey="interest" stackId="a" radius={[4, 4, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell key={`interest-${index}`} fill="hsl(var(--succeedo-orange) / 0.3)" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[hsl(var(--succeedo-orange))]" />
            <span className="font-mono text-[10px] uppercase text-muted-foreground">
              PRINCIPAL
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[hsl(var(--succeedo-orange))]/30" />
            <span className="font-mono text-[10px] uppercase text-muted-foreground">
              INTEREST
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

FinancingResultsPanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  calculations: PropTypes.object,
  loanAmount: PropTypes.number,
  interestRate: PropTypes.number,
  loanTerm: PropTypes.number,
  readOnly: PropTypes.bool,
};

FinancingResultsPanel.defaultProps = {
  calculations: null,
  loanAmount: 0,
  interestRate: 0,
  loanTerm: 0,
  readOnly: false,
};

export default FinancingResultsPanel;
